import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Upload,
  Copy,
  Trash2,
  ExternalLink,
  Eye,
  FileCode,
  Lock,
  Loader2,
  RefreshCcw,
  Sparkles,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBytes = (bytes) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const PrivatePagesManagement = () => {
  const { isAuthenticated, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Upload form state
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [shareDescription, setShareDescription] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverInputRef = useRef(null);

  // Edit state (per-row inline editing of share preview)
  const [editingSlug, setEditingSlug] = useState(null);
  const [editShareDesc, setEditShareDesc] = useState('');
  const [editCoverUrl, setEditCoverUrl] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    fetchPages();
    // eslint-disable-next-line
  }, [authLoading, isAuthenticated]);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` },
  });

  const fetchPages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/private-pages`, authHeaders());
      setPages(res.data || []);
    } catch (err) {
      toast.error('Erro ao carregar páginas');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    if (!title) {
      const baseName = f.name.replace(/\.html?$/i, '').replace(/_/g, ' ');
      setTitle(baseName);
    }
  };

  const handleCoverUpload = async (e, setter) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Selecione uma imagem');
      return;
    }
    setUploadingCover(true);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const res = await axios.post(`${API}/admin/upload`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      const filename = res.data.filename;
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/image/${filename}`;
      setter(fullUrl);
      toast.success('Imagem de capa carregada');
    } catch (err) {
      toast.error('Erro ao carregar imagem');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Selecione um ficheiro HTML');
      return;
    }
    if (!title.trim()) {
      toast.error('Indique um título');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    if (slug.trim()) formData.append('slug', slug.trim());
    if (description.trim()) formData.append('description', description);
    if (shareDescription.trim()) formData.append('share_description', shareDescription);
    if (coverImageUrl.trim()) formData.append('cover_image_url', coverImageUrl);

    try {
      const res = await axios.post(`${API}/admin/private-pages`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(`Página criada: /p/${res.data.slug}`);
      setFile(null);
      setTitle('');
      setSlug('');
      setDescription('');
      setShareDescription('');
      setCoverImageUrl('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (coverInputRef.current) coverInputRef.current.value = '';
      fetchPages();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro no upload';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const startEditing = (page) => {
    setEditingSlug(page.slug);
    setEditShareDesc(page.share_description || '');
    setEditCoverUrl(page.cover_image_url || '');
  };

  const cancelEditing = () => {
    setEditingSlug(null);
    setEditShareDesc('');
    setEditCoverUrl('');
  };

  const regenerateAutoPreview = async (slugToRegen) => {
    try {
      const res = await axios.post(
        `${API}/admin/private-pages/${slugToRegen}/auto-preview`,
        {},
        authHeaders()
      );
      if (res.data.cover_image_url || res.data.share_description) {
        toast.success('Preview gerado automaticamente do HTML');
        fetchPages();
      } else {
        toast.error('Não foi possível extrair preview deste HTML');
      }
    } catch (err) {
      toast.error('Erro ao regenerar preview');
    }
  };

  const saveEditing = async (slugToSave) => {
    setSavingEdit(true);
    const fd = new FormData();
    fd.append('share_description', editShareDesc);
    fd.append('cover_image_url', editCoverUrl);
    try {
      await axios.put(`${API}/admin/private-pages/${slugToSave}`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Preview WhatsApp atualizado');
      cancelEditing();
      fetchPages();
    } catch (err) {
      toast.error('Erro ao guardar');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (slugToDelete) => {
    if (!window.confirm(`Eliminar a página "${slugToDelete}"? Esta acção é irreversível.`)) return;
    try {
      await axios.delete(`${API}/admin/private-pages/${slugToDelete}`, authHeaders());
      toast.success('Página eliminada');
      fetchPages();
    } catch (err) {
      toast.error('Erro ao eliminar');
    }
  };

  const BACKEND_PUBLIC = process.env.REACT_APP_BACKEND_URL;

  const handleCopyShareLink = (slugToCopy) => {
    // Always copy the share URL with OG tags. This URL redirects browsers to /p/:slug
    // and serves Open Graph meta tags to crawlers (WhatsApp, Facebook, LinkedIn).
    const url = `${window.location.origin}/api/share/p/${slugToCopy}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiado! Funciona em WhatsApp, email e navegador.');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-brand-soft flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }
  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-brand-soft">
      {/* Header */}
      <div className="bg-white border-b border-brand-accent">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                asChild
                variant="outline"
                size="sm"
                className="border-brand-primary text-brand-primary"
                data-testid="back-to-dashboard-btn"
              >
                <Link to="/admin/dashboard">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Link>
              </Button>
              <div>
                <h1 className="text-lg sm:text-xl font-display font-semibold text-brand-text flex items-center gap-2">
                  <Lock className="w-5 h-5 text-brand-primary" />
                  Páginas Privadas
                </h1>
                <p className="text-xs sm:text-sm text-brand-text-secondary">
                  Brochuras off-market alojadas em yourbrokerportugal.com/p/...
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPages}
              data-testid="refresh-pages-btn"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        {/* Upload Card */}
        <Card className="mb-8 border-brand-accent/50">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <Upload className="w-5 h-5 text-brand-primary" />
              Nova Página Privada
            </CardTitle>
            <p className="text-sm text-brand-text-secondary">
              Faça upload de um ficheiro HTML (gerado pelo Claude, exportado, ou criado à mão).
              Imagens devem estar embutidas em base64 dentro do HTML.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-brand-text mb-1">
                  Ficheiro HTML <span className="text-red-500">*</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".html,.htm"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-brand-text file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-brand-primary file:text-white hover:file:bg-brand-primary-hover cursor-pointer"
                  data-testid="private-page-file-input"
                />
                {file && (
                  <p className="text-xs text-brand-text-secondary mt-1">
                    <FileCode className="w-3 h-3 inline mr-1" />
                    {file.name} ({formatBytes(file.size)})
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-text mb-1">
                    Título <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Ex: PIN Comporta · Apartamentos"
                    data-testid="private-page-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-text mb-1">
                    Slug <span className="text-brand-text-secondary text-xs">(opcional)</span>
                  </label>
                  <Input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="pin-comporta-apartamentos"
                    data-testid="private-page-slug-input"
                  />
                  <p className="text-xs text-brand-text-secondary mt-1">
                    URL final: yourbrokerportugal.com/p/<strong>{slug || '(auto do título)'}</strong>
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-text mb-1">
                  Descrição interna <span className="text-brand-text-secondary text-xs">(opcional, não aparece na página pública)</span>
                </label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Notas internas, cliente alvo, etc."
                  data-testid="private-page-desc-input"
                />
              </div>

              {/* WhatsApp Preview section */}
              <div className="bg-gradient-to-r from-brand-soft/60 to-amber-50/60 border border-brand-accent/40 rounded p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-brand-primary" />
                  <h4 className="text-sm font-semibold text-brand-text">Preview WhatsApp · Automático</h4>
                  <span className="text-[10px] uppercase tracking-wide bg-green-100 text-green-700 px-2 py-0.5 rounded">
                    Auto
                  </span>
                </div>
                <p className="text-xs text-brand-text-secondary">
                  Deixe estes campos vazios — o sistema extrai automaticamente a primeira imagem e
                  uma frase do seu HTML. Use os campos abaixo só se quiser personalizar.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-4 items-start">
                  <div>
                    <label className="block text-xs font-medium text-brand-text mb-1">
                      Imagem de capa <span className="text-brand-text-secondary">(opcional)</span>
                    </label>
                    <input
                      ref={coverInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleCoverUpload(e, setCoverImageUrl)}
                      className="hidden"
                      data-testid="cover-image-input"
                    />
                    <div
                      onClick={() => coverInputRef.current?.click()}
                      className="w-full aspect-[1.91/1] border-2 border-dashed border-brand-accent rounded cursor-pointer hover:border-brand-primary transition-colors flex items-center justify-center overflow-hidden bg-white"
                      data-testid="cover-image-dropzone"
                    >
                      {uploadingCover ? (
                        <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
                      ) : coverImageUrl ? (
                        <img src={coverImageUrl} alt="capa" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center text-brand-text-secondary p-2">
                          <Upload className="w-5 h-5 mx-auto mb-1 opacity-60" />
                          <span className="text-[10px]">Personalizar</span>
                        </div>
                      )}
                    </div>
                    {coverImageUrl && (
                      <button
                        type="button"
                        onClick={() => setCoverImageUrl('')}
                        className="text-xs text-red-600 hover:underline mt-1"
                      >
                        Remover
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-brand-text mb-1">
                      Frase de partilha <span className="text-brand-text-secondary">(opcional)</span>
                    </label>
                    <textarea
                      value={shareDescription}
                      onChange={(e) => setShareDescription(e.target.value)}
                      placeholder="Deixe vazio para extrair automaticamente do HTML, ou escreva uma frase personalizada"
                      rows={3}
                      maxLength={200}
                      className="w-full text-sm border border-brand-accent rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand-primary resize-none"
                      data-testid="share-description-input"
                    />
                    <p className="text-[10px] text-brand-text-secondary mt-1">
                      {shareDescription.length}/200 · Se vazio, será gerada automaticamente
                    </p>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={uploading || !file || !title.trim()}
                className="bg-brand-primary hover:bg-brand-primary-hover text-white"
                data-testid="upload-private-page-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    A carregar...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Publicar Página
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* List */}
        <div>
          <h2 className="text-lg font-display font-semibold text-brand-text mb-4">
            Páginas Publicadas ({pages.length})
          </h2>

          {loading ? (
            <div className="text-center py-12 text-brand-text-secondary">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              A carregar...
            </div>
          ) : pages.length === 0 ? (
            <Card className="border-dashed border-brand-accent">
              <CardContent className="py-12 text-center text-brand-text-secondary">
                <Lock className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>Ainda não publicou nenhuma página privada.</p>
                <p className="text-xs mt-1">Faça upload do seu primeiro HTML acima.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pages.map((p) => {
                const url = `${window.location.origin}/p/${p.slug}`;
                const hasPreview = !!(p.cover_image_url && p.share_description);
                const isEditing = editingSlug === p.slug;
                return (
                  <Card
                    key={p.slug}
                    className="border-brand-accent/50 hover:shadow-md transition-shadow"
                    data-testid={`private-page-card-${p.slug}`}
                  >
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        {p.cover_image_url ? (
                          <img
                            src={p.cover_image_url}
                            alt={p.title}
                            className="w-full md:w-32 aspect-[1.91/1] md:aspect-[1.91/1] object-cover rounded shrink-0 border border-brand-accent/40"
                          />
                        ) : (
                          <div className="w-full md:w-32 aspect-[1.91/1] rounded border border-dashed border-brand-accent shrink-0 flex items-center justify-center text-brand-text-secondary text-[10px] bg-brand-soft/40">
                            sem capa
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Lock className="w-4 h-4 text-brand-primary shrink-0" />
                            <h3 className="font-display text-base font-semibold text-brand-text truncate">
                              {p.title}
                            </h3>
                            {hasPreview ? (
                              <span className="text-[10px] uppercase tracking-wide bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                Preview WhatsApp ✓
                              </span>
                            ) : (
                              <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                Sem preview
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-brand-text-secondary font-mono truncate">
                            {url}
                          </p>
                          {p.share_description && !isEditing && (
                            <p className="text-xs text-brand-text mt-1">
                              <span className="font-medium">Frase:</span> {p.share_description}
                            </p>
                          )}
                          {p.description && (
                            <p className="text-xs text-brand-text-secondary mt-1 italic">
                              {p.description}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-3 mt-2 text-xs text-brand-text-secondary">
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {p.views || 0} visualizações
                            </span>
                            <span>{formatBytes(p.size_bytes)}</span>
                            <span>Criada {formatDate(p.created_at)}</span>
                          </div>

                          {/* Inline edit for share preview */}
                          {isEditing && (
                            <div className="mt-3 p-3 bg-brand-soft/60 border border-brand-accent rounded space-y-2">
                              <p className="text-xs font-semibold text-brand-text">Editar preview WhatsApp</p>
                              <input
                                type="text"
                                value={editCoverUrl}
                                onChange={(e) => setEditCoverUrl(e.target.value)}
                                placeholder="URL da imagem de capa"
                                className="w-full text-xs border border-brand-accent rounded px-2 py-1.5"
                                data-testid={`edit-cover-url-${p.slug}`}
                              />
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleCoverUpload(e, setEditCoverUrl)}
                                className="text-xs"
                              />
                              <textarea
                                value={editShareDesc}
                                onChange={(e) => setEditShareDesc(e.target.value)}
                                placeholder="Frase que aparece no WhatsApp"
                                rows={2}
                                maxLength={200}
                                className="w-full text-xs border border-brand-accent rounded px-2 py-1.5 resize-none"
                                data-testid={`edit-share-desc-${p.slug}`}
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => saveEditing(p.slug)}
                                  disabled={savingEdit}
                                  className="bg-brand-primary text-white"
                                  data-testid={`save-edit-${p.slug}`}
                                >
                                  {savingEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Guardar'}
                                </Button>
                                <Button size="sm" variant="outline" onClick={cancelEditing}>
                                  Cancelar
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 shrink-0 min-w-[160px]">
                          <Button
                            size="sm"
                            onClick={() => handleCopyShareLink(p.slug)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`copy-link-${p.slug}`}
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            Copiar Link
                          </Button>
                          {!hasPreview && (
                            <Button
                              size="sm"
                              onClick={() => regenerateAutoPreview(p.slug)}
                              className="bg-amber-500 hover:bg-amber-600 text-white"
                              data-testid={`auto-preview-${p.slug}`}
                            >
                              <Sparkles className="w-4 h-4 mr-1" />
                              Auto-gerar preview
                            </Button>
                          )}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => (isEditing ? cancelEditing() : startEditing(p))}
                              data-testid={`edit-${p.slug}`}
                              className="flex-1"
                            >
                              {isEditing ? 'Fechar' : 'Editar'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              asChild
                              data-testid={`open-link-${p.slug}`}
                            >
                              <a href={url} target="_blank" rel="noreferrer">
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(p.slug)}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`delete-${p.slug}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrivatePagesManagement;
